<script setup>
import { ref, computed, watch } from 'vue'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import DatePicker from 'primevue/datepicker'
import FloatLabel from 'primevue/floatlabel'
import Button from 'primevue/button'
import AutoComplete from 'primevue/autocomplete'
import { useToast } from 'primevue/usetoast'
import { useTasksStore } from '@/stores/tasks.js'
import { useEmployeeStore } from '@/stores/employee.js'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  contextAppLabel: { type: String, required: true },
  contextModel: { type: String, required: true },
  contextId: { type: [Number, String], required: true },
  defaultAssigneeId: { type: [Number, String], default: null },
  timezoneName: { type: String, default: Intl.DateTimeFormat().resolvedOptions().timeZone },
  contextTitle: { type: String, default: '' },
})


const RU_TITLES = {
  facility: 'Объект',
  work: 'Работа',
  contract: 'Договор',
  customer: 'Клиент',
}

const headerText = computed(() => {
const type = RU_TITLES[String(props.contextAppLabel || '').toLowerCase()] || props.contextAppLabel || 'Сущность'
const name = (props.contextTitle || '').trim()
  return name ? `Создать задачу по ${type} ${name}` : `Создать задачу по ${type}`
})



const emit = defineEmits(['update:modelValue', 'created'])

const toast = useToast()
const tasks = useTasksStore()
const employeesStore = useEmployeeStore()

const visible = ref(props.modelValue)
watch(() => props.modelValue, v => visible.value = v)
watch(visible, v => emit('update:modelValue', v))

// Поля формы
const title = ref('')
const description = ref('')
const dueDate = ref(null) // Date

// Исполнитель через employees-API
const selectedEmployee = ref(null) // { id, full_name, ... }
const suggestions = ref([])
const assigneeLoading = ref(false)
let searchTimer = null

watch(visible, async (v) => {
  if (v) {
    // Предзагрузка списка сотрудников и проставление дефолтного исполнителя
    await preloadEmployees()
    if (props.defaultAssigneeId && !selectedEmployee.value) {
      const id = Number(props.defaultAssigneeId)
      const it = (employeesStore.employees || []).find(e => Number(e.id) === id)
      if (it) selectedEmployee.value = mapEmployee(it)
    }
  } else {
    // очистка по закрытию
    title.value = ''
    description.value = ''
    dueDate.value = null
    selectedEmployee.value = null
    suggestions.value = []
  }
})

function mapEmployee(it) {
  return {
    id: it.id,
    full_name: it.full_name,
    department: it.department,
    post: it.post,
  }
}

async function preloadEmployees() {
  assigneeLoading.value = true
  try {
    await employeesStore.loadEmployees({ page: 1, is_active: true })
    const items = employeesStore.employees || []
    suggestions.value = items.map(mapEmployee)
  } catch (err) {
    suggestions.value = []
  } finally {
    assigneeLoading.value = false
  }
}

async function completeEmployees(e) {
  const q = String(e?.query ?? '').trim()
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(async () => {
    assigneeLoading.value = true
    try {
      await employeesStore.loadEmployees({ page: 1, search: q, is_active: true })
      const items = employeesStore.employees || []
      suggestions.value = items.map(mapEmployee)
    } catch (err) {
      suggestions.value = []
    } finally {
      assigneeLoading.value = false
    }
  }, 300)
}

function formatDateOnlyYYYYMMDD(d) {
  if (!d) return null
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function isPastDateInTZ(d, tz) {
  if (!d) return true
  try {
    const now = new Date()
    const todayStr = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now)
    const pickStr = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
    return pickStr < todayStr
  } catch {
    const today = new Date(); today.setHours(0,0,0,0)
    const dd = new Date(d); dd.setHours(0,0,0,0)
    return dd.getTime() < today.getTime()
  }
}

const saving = ref(false)

const payload = computed(() => {
  if (!title.value || !description.value || !selectedEmployee.value?.id || !dueDate.value) return null
  return {
    title: title.value.trim(),
    description: description.value.trim(),
    assignee: Number(selectedEmployee.value.id),
    due_date: formatDateOnlyYYYYMMDD(dueDate.value),
    context: {
      app_label: props.contextAppLabel,
      model: props.contextModel,
      id: Number(props.contextId),
    },
  }
})

async function submit() {
  if (!title.value?.trim()) {
    toast.add({ severity: 'warn', summary: 'Проверьте форму', detail: 'Укажите заголовок', life: 2500 }); return
  }
  if (title.value.trim().length > 200) {
    toast.add({ severity: 'warn', summary: 'Проверьте форму', detail: 'Заголовок не длиннее 200 символов', life: 2500 }); return
  }
  if (!description.value?.trim()) {
    toast.add({ severity: 'warn', summary: 'Проверьте форму', detail: 'Добавьте описание', life: 2500 }); return
  }
  if (!dueDate.value) {
    toast.add({ severity: 'warn', summary: 'Проверьте форму', detail: 'Выберите срок', life: 2500 }); return
  }
  if (isPastDateInTZ(dueDate.value, props.timezoneName)) {
    toast.add({ severity: 'warn', summary: 'Проверьте срок', detail: 'Дата не может быть в прошлом', life: 2500 }); return
  }
  if (!selectedEmployee.value?.id) {
    toast.add({ severity: 'warn', summary: 'Проверьте форму', detail: 'Выберите исполнителя', life: 2500 }); return
  }

  if (!payload.value) return
  saving.value = true
  try {
    const created = await tasks.create(payload.value)
    toast.add({ severity: 'success', summary: 'Задача создана', life: 2000 })
    emit('created', created)
    visible.value = false
  } catch (e) {
    const detail = e?.response?.data?.detail || e?.message || 'Не удалось создать задачу'
    toast.add({ severity: 'error', summary: 'Ошибка создания', detail, life: 4000 })
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <Dialog v-model:visible="visible" :header="headerText" :modal="true" :style="{ width: '520px', maxWidth: '95vw' }" position="top">
    <form @submit.prevent="submit" class="flex flex-col gap-5 p-fluid pt-2">
      <!-- 1) Заголовок -->
      <FloatLabel variant="on">
        <InputText id="task_title" v-model.trim="title" maxlength="200" class="w-full" />
        <label for="task_title">Заголовок</label>
      </FloatLabel>

      <!-- 2) Описание -->
      <FloatLabel variant="on">
        <Textarea id="task_description" v-model="description" autoResize rows="4" class="w-full" />
        <label for="task_description">Описание</label>
      </FloatLabel>

      <!-- 3) Дата -->
      <FloatLabel variant="on">
        <DatePicker id="task_due" v-model="dueDate" dateFormat="yy-mm-dd" :minDate="new Date()" showIcon fluid />
        <label for="task_due">Срок (дата)</label>
      </FloatLabel>

      <!-- 4) Исполнитель (employees) -->
      <FloatLabel variant="on">
        <AutoComplete
            id="task_assignee"
            v-model="selectedEmployee"
            optionLabel="full_name"
            :suggestions="suggestions"
            :loading="assigneeLoading"
            :dropdown="true"
            :forceSelection="true"
            :completeOnFocus="true"
            @complete="completeEmployees"
            class="w-full"
        >
          <template #option="{ option }">
            <div class="flex items-center gap-2">
              <div class="flex flex-col">
                <span>{{ option.full_name }}</span>
                <small class="text-gray-500">{{ option.department }}<span v-if="option.post"> • {{ option.post }}</span></small>
              </div>
            </div>
          </template>
        </AutoComplete>
        <label for="task_assignee">Исполнитель</label>
      </FloatLabel>

      <div class="flex justify-center gap-4 pt-2">
        <Button label="Создать" type="submit" severity="info" rounded :loading="saving" />
        <Button label="Отмена" type="button" severity="secondary" rounded :disabled="saving" @click="visible=false" />
      </div>
    </form>
  </Dialog>
</template>